import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { authAPI } from '../../services/api';
import './MyAccount.css';

function MyAccount({ user }) {
  const { fetchUserProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Normalize any legacy/odd role strings coming from older tokens or DB values.
  const normalizedRole = useMemo(() => {
    const raw = (user?.role ?? '').toString().trim();
    if (!raw) return 'PLAYER';

    const upper = raw.toUpperCase();
    if (upper === 'GM' || upper === 'GAME MASTER' || upper === 'GAMEMASTER') return 'GM';
    if (upper === 'PLAYER') return 'PLAYER';
    if (upper === 'ADMIN') return 'ADMIN';
    return upper;
  }, [user?.role]);

  const ROLE_LABEL = useMemo(
    () => ({
      PLAYER: 'Player',
      GM: 'Game Master',
      ADMIN: 'Administrator'
    }),
    []
  );

  // Selector state (kept in sync with profile refreshes)
  const [selectedRole, setSelectedRole] = useState(normalizedRole);
  useEffect(() => {
    setSelectedRole(normalizedRole);
  }, [normalizedRole]);

  const updateRole = async (nextRole) => {
    if (!user) return;
    if (user?.role === 'ADMIN') return;
    if (nextRole === normalizedRole) return;

    const confirmMessage = nextRole === 'GM'
      ? 'Switch to Game Master role? This will allow you to create and manage campaigns.'
      : 'Switch to Player role? You will no longer be able to create campaigns.';

    if (!window.confirm(confirmMessage)) {
      // Revert the selector UI back to the current role.
      setSelectedRole(normalizedRole);
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await authAPI.updateRole(nextRole);

      // Update token in localStorage
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
      }

      // Refresh user profile
      await fetchUserProfile();

      setMessage({
        type: 'success',
        text: `Role updated to ${ROLE_LABEL[nextRole] || nextRole}.`
      });

      // Clear message after 3 seconds
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error updating role:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to update role. Please try again.'
      });
      // Revert selector on failure.
      setSelectedRole(normalizedRole);
    } finally {
      setLoading(false);
    }
  };

  const getRoleDescription = (role) => {
    switch (role) {
      case 'ADMIN':
        return 'Administrator - Full access to manage the platform';
      case 'GM':
        return 'Game Master - Can create and manage campaigns';
      case 'PLAYER':
        return 'Player - Can join campaigns and create characters';
      default:
        return role;
    }
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'ADMIN':
        return 'role-badge-admin';
      case 'GM':
        return 'role-badge-gm';
      case 'PLAYER':
      default:
        return 'role-badge-player';
    }
  };

  return (
    <div className="my-account-section">
      <div className="section-header">
        <h2>👤 My Account</h2>
      </div>

      {/* Success/Error Messages */}
      {message.text && (
        <div className={`message-box ${message.type === 'success' ? 'success-box' : 'error-box'}`}>
          {message.type === 'success' ? '✅' : '❌'} {message.text}
        </div>
      )}

      {/* Account Information Card */}
      <div className="account-card">
        <h3>Account Information</h3>
        
        <div className="account-details">
          <div className="detail-row">
            <span className="detail-label">Username:</span>
            <span className="detail-value">{user?.username}</span>
          </div>

          <div className="detail-row">
            <span className="detail-label">Email:</span>
            <span className="detail-value">{user?.email}</span>
          </div>

          <div className="detail-row">
            <span className="detail-label">Account Type:</span>
            <span className="detail-value">{user?.provider || 'Local'}</span>
          </div>

          <div className="detail-row">
            <span className="detail-label">Member Since:</span>
            <span className="detail-value">
              {user?.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }) : 'N/A'}
            </span>
          </div>
        </div>
      </div>

      {/* Role Management Card */}
      <div className="account-card">
        <h3>Role Management</h3>
        
        <div className="role-info">
          <div className="current-role">
            <span className="role-label">Current Role:</span>
            <span className={`role-badge ${getRoleBadgeClass(normalizedRole)}`}>
              {ROLE_LABEL[normalizedRole] || normalizedRole}
            </span>
          </div>
          
          <p className="role-description">
            {getRoleDescription(normalizedRole)}
          </p>

          {/* Player / Game Master selector - Only show if not admin */}
          {normalizedRole !== 'ADMIN' && (
            <div className="role-toggle-section">
              <div className="toggle-info">
                <h4>
                  {normalizedRole === 'PLAYER' ? '🎲 Want to run campaigns?' : '👥 Want to focus on playing?'}
                </h4>
                <p>
                  {normalizedRole === 'PLAYER' 
                    ? 'Switch to Game Master role to create and manage your own campaigns. You can run games for your friends and control the story!'
                    : 'Switch back to Player role if you want to focus on playing characters in campaigns run by others.'
                  }
                </p>
              </div>

              <p className="toggle-note">
                {loading
                  ? 'Updating role…'
                  : 'Click a role card below to switch. You can switch back at any time.'}
                {normalizedRole === 'GM' && !loading ? ' Your existing campaigns will be preserved.' : ''}
              </p>
            </div>
          )}

          {/* Admin Notice */}
          {normalizedRole === 'ADMIN' && (
            <div className="admin-notice">
              <p>
                ⚠️ As an administrator, you cannot change your own role. If you need to modify your role, please contact another administrator or update it directly in the database.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Benefits Card */}
      <div className="account-card">
        <h3>Role Benefits</h3>
        
        <div className="benefits-grid">
          <div
            className={`benefit-column role-option ${selectedRole === 'PLAYER' ? 'selected' : ''} ${loading ? 'disabled' : ''}`}
            role="button"
            tabIndex={0}
            aria-pressed={selectedRole === 'PLAYER'}
            onClick={() => {
              if (loading || normalizedRole === 'ADMIN') return;
              setSelectedRole('PLAYER');
              updateRole('PLAYER');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (loading || normalizedRole === 'ADMIN') return;
                setSelectedRole('PLAYER');
                updateRole('PLAYER');
              }
            }}
          >
            <h4>🎮 Player</h4>
            <ul>
              <li>Create unlimited characters</li>
              <li>Join campaigns</li>
              <li>Access all game resources</li>
              <li>Track character progression</li>
            </ul>
          </div>

          <div
            className={`benefit-column role-option ${selectedRole === 'GM' ? 'selected' : ''} ${loading ? 'disabled' : ''}`}
            role="button"
            tabIndex={0}
            aria-pressed={selectedRole === 'GM'}
            onClick={() => {
              if (loading || normalizedRole === 'ADMIN') return;
              setSelectedRole('GM');
              updateRole('GM');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (loading || normalizedRole === 'ADMIN') return;
                setSelectedRole('GM');
                updateRole('GM');
              }
            }}
          >
            <h4>🎲 Game Master</h4>
            <ul>
              <li><strong>All Player benefits, plus:</strong></li>
              <li>Create and manage campaigns</li>
              <li>Invite players to campaigns</li>
              <li>Control story and encounters</li>
              <li>Manage game sessions</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MyAccount;
